/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import useStyles from './style';
import { Grid, Typography } from "@material-ui/core";
import {FormattedMessage, useIntl} from "react-intl";
import {localStoreOperation} from "../../../data/utils";
import {StorageOperations} from "../../../data/interfaces";
import {FIDP, RESOURCE_SIGN_IN} from "../../../data/constants";
import Settings from "../../../conf/settings";
import {verifyInvitation} from "../../../data/operations";
import LoginCard from "../../login/loginCard";

interface IVerifyInvitation {
  email: string;
  handle: string;
  roles: string[];
}

function AcceptInvitation() {
  const classes = useStyles();
  const history = useHistory();
  const intl = useIntl();
  const params = new URLSearchParams(window.location.search);
  const confirmationKey = params.get('confirmation-key') || '';
  const confirmationOrg = params.get('organization') || '';
  const displayOrgName = params.get('organization-name')?.replace('+', ' ');
  const fidp =
    params.get('fidp') === 'local'
      ? 'LOCAL'
      : params.get('fidp')?.toLowerCase();

  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const successMsg = intl.formatMessage({
    defaultMessage: `You have been invited to the organization `,
    id: 'app.org.invitation.accept.success.label',
  });
  const invalidUrlMsg = intl.formatMessage({
    defaultMessage: `Your invitation is invalid.`,
    id: 'app.org.invitation.accept.invalid.label',
  });
  const loadingMsg = intl.formatMessage({
    defaultMessage: `Verifying invitation.`,
    id: 'app.org.invitation.accept.loading.label',
  });
  const checkAgain = intl.formatMessage({
    defaultMessage: `Please check the link again.`,
    id: 'app.org.invitation.accept.checkAgain.label',
  });

  const invitationMessage = isVerified ? successMsg : `${invalidUrlMsg} ${checkAgain}`;
  const defaultMessageText = !isLoading ? invitationMessage : loadingMsg;
  const orgName = !isLoading && isVerified && `"${displayOrgName}"`;

  useEffect(() => {
    if (confirmationOrg && confirmationKey) {
      setIsLoading(true);
      verifyInvitation(confirmationOrg, confirmationKey)
          .then((response) => {
            const verification = response.data as IVerifyInvitation;
            setIsVerified(true);
          })
          .catch(() => {
            setIsVerified(false);
          })
          .finally(() => {
            setIsLoading(false);
            if (fidp) {
              localStoreOperation(StorageOperations.ADD, FIDP, fidp);
              history.replace(RESOURCE_SIGN_IN, {
                confirmationKey,
                confirmationOrg,
              });
            }
          });
    }
  }, []);

  const handleEmailLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.emailFidp);
    history.replace(RESOURCE_SIGN_IN, {
      confirmationKey,
      confirmationOrg,
    });
  };

  const handleGithubLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.githubFidp);
    history.replace(RESOURCE_SIGN_IN, {
      confirmationKey,
      confirmationOrg,
    });
  };

  const handleGoogleLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.googleFidp);
    history.replace(RESOURCE_SIGN_IN, {
          confirmationKey,
          confirmationOrg,
    });
  };

  const handleMicrosoftLogin = () => {
    localStoreOperation(StorageOperations.ADD, FIDP, Settings.microsoftFidp);
    history.replace(RESOURCE_SIGN_IN, {
          confirmationKey,
          confirmationOrg,
    });
  };

  return (
    <div className={classes.loginBgLogin}>
      <Grid container justifyContent={"center"} alignItems={"center"} className={classes.rectangle}>
        <Grid item xs={12}>
          <div className={classes.choreoLoginLogo}>
            <img alt="WSO2 Developer Platform Logo Black" src={"/themes/default/images/logo-black.svg"} />
          </div>
        </Grid>
        <Grid item xs={12} container justifyContent={"center"} alignItems={"center"}>
          <div>
            <Typography className={classes.inviteMsg} variant="h6">
              {defaultMessageText}
            </Typography>
            <Typography className={classes.orgName} variant="h5">
              {orgName}
            </Typography>
          </div>
          {!isLoading && isVerified && (
              <Grid container justifyContent={"center"} alignItems={"center"}>
                <Grid item xs={12}>
                  <Typography className={classes.choreoSignInText}>
                    <FormattedMessage
                        id="authentication.login.signIn.title"
                        defaultMessage="Sign in to continue"
                    />
                  </Typography>
                </Grid>
                <LoginCard
                  handleEmailLogin={handleEmailLogin}
                  handleGithubLogin={handleGithubLogin}
                  handleGoogleLogin={handleGoogleLogin}
                  handleMicrosoftLogin={handleMicrosoftLogin}
                />
              </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default AcceptInvitation;
